import React, { useState, useEffect } from 'react'
import { 
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import { getApiEndpoint } from '../../lib/api'
import toast from 'react-hot-toast'
import authAPI from '../../lib/authAPI'

const UserDetailModal = ({ user, isOpen, onClose, onRefresh }) => {
  const [modules, setModules] = useState([])
  const [userPermissions, setUserPermissions] = useState({})
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(new Set())

  useEffect(() => {
    if (isOpen && user) {
      fetchUserData()
    }
  }, [isOpen, user])

  const fetchUserData = async () => {
    try {
      setLoading(true)

      const response = await fetch(getApiEndpoint('ADMIN_MODULE_PERMISSIONS'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authAPI.token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      
      if (data.error) {
        throw new Error(data.message || data.error)
      }

      setModules(data.modules || [])
      
      // Build user permissions map
      const permissionsMap = {}
      data.permissions.forEach(permission => {
        if (permission.user_id === user.id) {
          permissionsMap[permission.module_id] = true
        }
      })
      setUserPermissions(permissionsMap)

    } catch (error) {
      console.error('❌ Failed to fetch user data:', error)
      toast.error('Failed to load user permissions')
    } finally {
      setLoading(false)
    }
  }


  const togglePermission = async (moduleId) => {
    if (user.is_admin || updating.has(moduleId)) return

    try {
      setUpdating(prev => new Set([...prev, moduleId]))
      
      const hasPermission = userPermissions[moduleId]
      const endpoint = getApiEndpoint('ADMIN_MODULE_PERMISSIONS')
      const method = hasPermission ? 'DELETE' : 'POST'
      
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${authAPI.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: user.id,
          module_id: moduleId,
          notes: hasPermission ? 'Revoked via user detail modal' : 'Granted via user detail modal'
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const result = await response.json()
      
      if (result.error) {
        throw new Error(result.message || result.error)
      }

      // Update local state
      setUserPermissions(prev => ({
        ...prev,
        [moduleId]: !hasPermission
      }))

      toast.success(hasPermission ? 'Permission revoked' : 'Permission granted')

    } catch (error) {
      console.error('❌ Failed to toggle permission:', error)
      toast.error(`Failed to ${userPermissions[moduleId] ? 'revoke' : 'grant'} permission`)
    } finally {
      setUpdating(prev => {
        const newSet = new Set(prev)
        newSet.delete(moduleId)
        return newSet
      })
    }
  }


  const getUserStatusBadge = () => {
    if (user.is_admin) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
          <ShieldCheckIcon className="w-4 h-4 mr-2" />
          Administrator
        </span>
      )
    }

    switch (user.status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-4 h-4 mr-2" />
            Approved
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-4 h-4 mr-2" />
            Pending Approval
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            Unknown Status
          </span>
        )
    }
  }

  // Group modules by category
  const modulesByCategory = modules.reduce((acc, module) => {
    if (!acc[module.category]) {
      acc[module.category] = []
    }
    acc[module.category].push(module)
    return acc
  }, {})

  const categoryOrder = ['assessment', 'reports', 'assets', 'services', 'tools', 'integrations', 'settings', 'admin']
  const categoryLabels = {
    assessment: 'Assessment Modules',
    reports: 'Reports Modules', 
    assets: 'Assets Modules',
    services: 'Services Modules',
    tools: 'Tools Modules',
    integrations: 'Integration Modules',
    settings: 'Settings Modules',
    admin: 'Administration Modules'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-10 w-10">
                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                  <UserIcon className="h-6 w-6 text-gray-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  User Permissions - {user.email}
                </h3>
                <div className="mt-1">
                  {getUserStatusBadge()}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>


          {/* Admin Notice */}
          {user.is_admin && (
            <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center">
                <ShieldCheckIcon className="h-5 w-5 text-purple-600 mr-2" />
                <span className="text-sm text-purple-800 font-medium">
                  This user is an administrator and has access to all modules automatically.
                </span>
              </div>
            </div>
          )}

          {/* Module Permissions */}
          <div className="space-y-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-sm text-gray-600">Loading permissions...</p>
              </div>
            ) : (
              categoryOrder.map(category => {
                const categoryModules = modulesByCategory[category] || []
                if (categoryModules.length === 0) return null

                return (
                  <div key={category} className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      {categoryLabels[category] || category}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {categoryModules.map(module => {
                        const hasPermission = user.is_admin || userPermissions[module.id]
                        const isUpdating = updating.has(module.id)

                        return (
                          <div
                            key={module.id}
                            className={`relative rounded-lg border p-4 hover:shadow-sm transition-all ${
                              hasPermission
                                ? 'border-green-200 bg-green-50'
                                : 'border-gray-200 bg-white'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h5 className="text-sm font-medium text-gray-900">
                                  {module.display_name}
                                </h5>
                                {module.description && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {module.description}
                                  </p>
                                )}
                              </div>
                              
                              <button
                                onClick={() => togglePermission(module.id)}
                                disabled={user.is_admin || isUpdating}
                                className={`ml-3 flex-shrink-0 w-10 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                                  user.is_admin
                                    ? 'bg-purple-200 cursor-not-allowed'
                                    : hasPermission
                                      ? 'bg-green-500 hover:bg-green-600'
                                      : 'bg-gray-200 hover:bg-gray-300'
                                }`}
                              >
                                <div
                                  className={`w-4 h-4 rounded-full bg-white transition-transform duration-200 ease-in-out mt-1 ${
                                    hasPermission ? 'translate-x-5' : 'translate-x-1'
                                  }`}
                                ></div>
                              </button>
                            </div>
                            
                            {isUpdating && (
                              <div className="absolute inset-0 bg-white bg-opacity-50 rounded-lg flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 flex justify-end">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserDetailModal
